import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, ArrowLeft, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      toast.success('Password reset successful. Please sign in.');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not reset password. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] px-4">
      <div className="w-full max-w-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl shadow-lg p-8" data-testid="reset-password-page">
        {!token ? (
          <div className="text-center py-4" data-testid="reset-no-token">
            <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Invalid reset link</h1>
            <p className="text-[hsl(var(--muted-foreground))] text-sm mb-6">This link is missing its token. Please request a new password reset.</p>
            <Link to="/forgot-password" className="text-[hsl(var(--primary))] font-medium hover:underline" data-testid="request-new-link">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="text-center py-4" data-testid="reset-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Password updated</h1>
            <p className="text-[hsl(var(--muted-foreground))] text-sm">Redirecting you to sign in...</p>
          </div>
        ) : (
          <>
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] mb-6">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))]/10 flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-[hsl(var(--primary))]" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Set a new password</h1>
            <p className="text-[hsl(var(--muted-foreground))] text-sm mb-6">Choose a strong password you haven't used before.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="password">New password</label>
                <input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                  placeholder="At least 6 characters"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="confirm">Confirm password</label>
                <input
                  id="confirm" type="password" required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                  placeholder="Re-enter your password"
                  data-testid="reset-confirm-input"
                />
              </div>
              <button
                type="submit" disabled={loading}
                className="w-full bg-[hsl(var(--primary))] text-white py-2.5 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                data-testid="reset-submit-button"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating...</> : 'Reset password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
